/**
 * Cliente HTTP da API.
 *
 * - O access token (JWT) fica só em memória, nunca em localStorage.
 * - O refresh token fica num cookie HttpOnly que o JavaScript não lê; a sessão
 *   é restaurada ao abrir a página chamando o token endpoint com
 *   grant_type=refresh_token.
 * - Um 401 dispara UMA renovação compartilhada entre as requisições em
 *   andamento; se falhar, a sessão é encerrada.
 */
import type { RespostaToken } from "../../../shared/tipos";

export class ErroHttp extends Error {
  constructor(
    public status: number,
    mensagem: string,
  ) {
    super(mensagem);
  }
}

let accessToken: string | null = null;
let expiraEm = 0;
let renovacao: Promise<boolean> | null = null;
let aoExpirarSessao: (() => void) | null = null;

export function definirAoExpirarSessao(callback: () => void) {
  aoExpirarSessao = callback;
}

function guardarToken(resposta: RespostaToken) {
  accessToken = resposta.access_token;
  expiraEm = Date.now() + resposta.expires_in * 1000;
}

function limparToken() {
  accessToken = null;
  expiraEm = 0;
}

async function chamarTokenEndpoint(parametros: Record<string, string>): Promise<Response> {
  return fetch("/api/oauth/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(parametros),
    credentials: "same-origin",
  });
}

async function mensagemDeErro(resposta: Response): Promise<string> {
  try {
    const corpo = await resposta.json();
    return corpo.erro ?? corpo.error_description ?? `Erro ${resposta.status}`;
  } catch {
    return `Erro ${resposta.status}`;
  }
}

/** grant_type=password */
export async function entrar(email: string, senha: string): Promise<void> {
  const resposta = await chamarTokenEndpoint({ grant_type: "password", username: email, password: senha });
  if (!resposta.ok) throw new ErroHttp(resposta.status, await mensagemDeErro(resposta));
  guardarToken(await resposta.json());
}

/** grant_type=refresh_token (cookie HttpOnly). Chamadas simultâneas compartilham a mesma renovação. */
export function renovarSessao(): Promise<boolean> {
  if (!renovacao) {
    renovacao = (async () => {
      try {
        let resposta = await chamarTokenEndpoint({ grant_type: "refresh_token" });
        if (resposta.status === 400 && resposta.headers.has("retry-after")) {
          // Outra aba acabou de rotacionar o refresh token (a API sinaliza com
          // Retry-After): o cookie já foi atualizado, então repetir resolve.
          await new Promise((r) => setTimeout(r, 400));
          resposta = await chamarTokenEndpoint({ grant_type: "refresh_token" });
        }
        if (!resposta.ok) {
          limparToken();
          return false;
        }
        guardarToken(await resposta.json());
        return true;
      } catch {
        return false;
      } finally {
        renovacao = null;
      }
    })();
  }
  return renovacao;
}

export async function sair(): Promise<void> {
  try {
    await fetch("/api/oauth/revoke", { method: "POST", credentials: "same-origin" });
  } finally {
    limparToken();
  }
}

/** Usado após trocar a senha: a API devolve tokens novos. */
export function aplicarNovoToken(resposta: RespostaToken) {
  guardarToken(resposta);
}

async function requisicaoAutenticada(caminho: string, init: RequestInit, tentarRenovar = true): Promise<Response> {
  if (!accessToken || Date.now() > expiraEm - 60_000) await renovarSessao();
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const resposta = await fetch(caminho, { ...init, headers, credentials: "same-origin" });

  if (resposta.status === 401 && tentarRenovar) {
    if (await renovarSessao()) return requisicaoAutenticada(caminho, init, false);
    aoExpirarSessao?.();
  }
  return resposta;
}

export async function api<T>(caminho: string, opcoes: { method?: string; body?: unknown } = {}): Promise<T> {
  const init: RequestInit = { method: opcoes.method ?? "GET" };
  if (opcoes.body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(opcoes.body);
  }
  const resposta = await requisicaoAutenticada(caminho, init);
  if (!resposta.ok) throw new ErroHttp(resposta.status, await mensagemDeErro(resposta));
  return (await resposta.json()) as T;
}

/** Requisição pública (sem Bearer), ex.: registro de conta. */
export async function apiPublica<T>(caminho: string, body: unknown): Promise<T> {
  const resposta = await fetch(caminho, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resposta.ok) throw new ErroHttp(resposta.status, await mensagemDeErro(resposta));
  return (await resposta.json()) as T;
}

/** Baixa um arquivo autenticado (o link direto não levaria o Bearer). */
export async function baixarArquivo(caminho: string, nomePadrao: string): Promise<void> {
  const resposta = await requisicaoAutenticada(caminho, { method: "GET" });
  if (!resposta.ok) throw new ErroHttp(resposta.status, await mensagemDeErro(resposta));
  const disposicao = resposta.headers.get("content-disposition") ?? "";
  const nome = /filename\*=UTF-8''([^;]+)/.exec(disposicao)?.[1];
  const url = URL.createObjectURL(await resposta.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = nome ? decodeURIComponent(nome) : nomePadrao;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
